<template>
  <div>
    <Head title="Groups" />
    <h1 class="mb-8 text-3xl font-bold">Groups</h1>
    <div class="flex items-center justify-between mb-6">
      <search-filter v-model="form.search" class="mr-4 w-full max-w-md" @reset="reset" />
      <Link class="btn-orange" href="/groups/create">
        <span>Create</span>
        <span class="hidden md:inline">&nbsp;Group</span>
      </Link>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th width="5%" class="pb-3 pt-3 px-2">#</th>
          <th class="pb-3 pt-3 px-2">Group Name</th>
          <th class="pb-3 pt-3 px-2 text-center">Permissions</th>
          <th class="pb-3 pt-3 px-2" />
        </tr>
        <tr v-for="(role, index) in roles.data" :key="role.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
          <td class="border-t">
            <Link class="flex items-center px-2 py-2 focus:text-indigo-500" :href="`/groups/${role.id}/edit`">
              {{ index+1 }}
            </Link>
          </td>
          <td class="border-t">
            <Link class="flex items-center px-2 py-2 focus:text-indigo-500" :href="`/groups/${role.id}/edit`">
              {{ role.name }}
            </Link>
          </td>
          <td class="border-t">
            <a v-if="role.permissions.length" href="#" class="flex items-center justify-center w-full cursor:pointer" @click="getPermissions(role)">
              <icon name="view" class="block w-6 h-6 fill-gray-400" /> &nbsp; <sup><small>{{ role.permissions.length }}</small></sup>
            </a>
            <span v-else class="flex items-center justify-center w-full">-</span>
          </td>
          <td class="w-px border-t">
            <Link class="flex items-center px-4" :href="`/groups/${role.id}/edit`" tabindex="-1">
              <icon name="edit-1" class="block w-6 h-6 fill-gray-400" />
            </Link>
          </td>
        </tr>
        <tr v-if="roles.data.length === 0">
          <td class="px-1 py-1 border-t" colspan="4">No data found</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-6" :links="roles.links" />
  </div>
  <div v-if="isOpen" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div class="w-1/2 inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 md:px-6 sm:flex md:flex modal-wrapper w-full">
            <h1 style="font-size:30px;">
              {{ selectedGroup.name }} - Permissions
            </h1>
          </div>
          <div v-if="groupPermissions.length > 0" class="bg-white px-4 pt-0 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              <table class="w-full whitespace-nowrap" width="80%">
                <tr class="text-left font-bold">
                  <th class="pb-3 pt-3 px-2">#</th>
                  <th class="pb-3 pt-3 px-2">Permission</th>
                  <th class="pb-3 pt-3 px-2 flex items-center justify-end">Manage</th>
                </tr>
                <tr v-for="(item, index) in groupPermissions" :key="index">
                  <td class="w-px border-t pb-2 pt-2">
                    <span class="flex items-center px-2">{{ index+1 }}</span>
                  </td>
                  <td class="w-px border-t pb-2 pt-2">
                    <span class="flex items-left px-2">{{ item.name }}</span>
                  </td>
                  <td class="w-px border-t pb-2 pt-2">
                    <a href="#" class="flex items-center justify-end w-full px-2">
                      <icon name="times" class="block w-6 h-6 fill-gray-400" />
                    </a>
                  </td>
                </tr>
              </table>
            </div>
          </div>
          <div v-else class="p-5">No group permissions found.</div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5" @click="closeModal">
                Close
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import Pagination from '@/Shared/Pagination'

export default {
  components: {
    Head,
    Link,
    Icon,
    SearchFilter,
    Pagination,
  },
  layout: Layout,
  props: {
    filters: Object,
    roles: Object,
  },
  data() {
    return {
      form: this.$inertia.form({
        search: this.filters.search,
      }),
      selectedGroup: null,
      groupPermissions: [],
      isOpen: false,
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/groups', pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    getPermissions(item) {
      this.isOpen = true
      this.selectedGroup = item
      this.groupPermissions = item.permissions
    },
    closeModal() {
      this.isOpen = false
    },
  },
}
</script>

<style>

</style>